/**
 * AdminFeatureFlagsPage — Toggle features without code deploy.
 *
 * /admin/feature-flags
 *   - List default + custom flags
 *   - Toggle enabled state
 *   - Save tracks who flipped what and when (via audit log + updated_by field)
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Flag, CheckCircle2, CircleOff } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AdminLayout from "@/components/AdminLayout";

export default function AdminFeatureFlagsPage() {
    const { user, loading: authLoading } = useAuth() || {};
    const navigate = useNavigate();
    const [flags, setFlags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyKey, setBusyKey] = useState(null);

    useEffect(() => {
        if (authLoading) return;
        if (!user || !["super_admin", "admin"].includes(user.role)) return navigate("/login");
        load();
    }, [user, authLoading]); // eslint-disable-line

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/feature-flags");
            setFlags(r.data || []);
        } finally { setLoading(false); }
    };

    const toggle = async (flag) => {
        setBusyKey(flag.key);
        try {
            await api.put(`/admin/feature-flags/${flag.key}`, {
                enabled: !flag.enabled,
                label: flag.label,
                description: flag.description,
            });
            load();
        } catch (e) { alert(e.response?.data?.detail || "Güncellenemedi"); }
        finally { setBusyKey(null); }
    };

    return (
        <AdminLayout title="Özellik Bayrakları">
            <div className="mb-4 p-3 rounded-xl bg-sky-50 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-900 text-xs text-slate-600 dark:text-slate-300">
                <strong className="text-sky-700 dark:text-sky-300">Feature Flags:</strong> Kod deploy etmeden özellikleri açıp kapatın. Yeni geliştirilen özellikleri önce beta'da test etmek veya bir sorun durumunda anında devre dışı bırakmak için kullanın.
            </div>
            {loading ? <div>Yükleniyor...</div> : (
                <div className="space-y-2" data-testid="flags-list">
                    {flags.map((f) => (
                        <div key={f.key} data-testid={`flag-${f.key}`}
                            className={`flex items-center gap-4 p-4 rounded-2xl border-2 transition-colors ${f.enabled ? "border-emerald-200 dark:border-emerald-900 bg-emerald-50/50 dark:bg-emerald-950/10" : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"}`}>
                            <div className="flex-shrink-0">
                                {f.enabled ? <CheckCircle2 className="h-7 w-7 text-emerald-500" /> : <CircleOff className="h-7 w-7 text-slate-400" />}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <h3 className="font-display font-bold text-slate-900 dark:text-white">{f.label || f.key}</h3>
                                    <code className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-800 text-slate-600">{f.key}</code>
                                </div>
                                {f.description && <p className="text-xs text-slate-500 mt-1">{f.description}</p>}
                                {f.updated_at && (
                                    <p className="text-[10px] text-slate-400 mt-1">Son değişiklik: {f.updated_at.slice(0, 16).replace("T", " ")}{f.updated_by ? ` · ${f.updated_by}` : ""}</p>
                                )}
                            </div>
                            <button
                                data-testid={`toggle-${f.key}`}
                                onClick={() => toggle(f)}
                                disabled={busyKey === f.key}
                                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors disabled:opacity-50 ${f.enabled ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-600"}`}
                            >
                                <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${f.enabled ? "translate-x-6" : "translate-x-1"}`} />
                            </button>
                        </div>
                    ))}
                </div>
            )}
            <div className="mt-6 text-[11px] italic text-slate-400">
                💡 İpucu: Frontend'de `useFeatureFlag("key")` hook'unu kullanarak buradaki ayarları kullanıcı arayüzüne yansıtabilirsiniz.
            </div>
        </AdminLayout>
    );
}
